import { Box } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { EligibilityCriteriaCalculationGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { BorrowingBaseSummaryAccordion } from './BorrowingBaseSummaryAccordion';
import { BorrowingBaseSummaryAccordionRow } from './BorrowingBaseSummaryAccordionRow';
import { BorrowingBaseSummaryAccordionSubRow } from './BorrowingBaseSummaryAccordionSubRow';
import {
  getColumnValuesForLoanProductCalculationsField,
  getNumberedSubRowLabel,
  LoanProductCalculationsBorrowingBaseSummaryInput,
} from './utils';

type BorrowingBaseSummaryEligibilityProps = {
  loanProductCalculations: LoanProductCalculationsBorrowingBaseSummaryInput;
  eligibilityCriteriaCalculationsForLoanProduct: {
    minuendBorrowingBaseReport: EligibilityCriteriaCalculationGQLType[];
    subtrahendBorrowingBaseReport?: EligibilityCriteriaCalculationGQLType[];
    referenceBorrowingBaseReport?: EligibilityCriteriaCalculationGQLType[];
  };
  loanProductSlug: string;
  loanProductId: string;
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
  defaultIsExpanded: boolean;
};
export const BorrowingBaseSummaryEligibility = ({
  loanProductCalculations,
  eligibilityCriteriaCalculationsForLoanProduct,
  loanProductSlug,
  loanProductId,
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
  defaultIsExpanded,
}: BorrowingBaseSummaryEligibilityProps) => {
  const totalEligibilityKickoutForLps = getColumnValuesForLoanProductCalculationsField(
    {
      loanProductCalculationsForBorrowingBaseReports: loanProductCalculations,
      loanProductId,
      fieldName: 'totalEligibilityKickout',
    },
  );

  return (
    <BorrowingBaseSummaryAccordion
      defaultIsExpanded={defaultIsExpanded}
      accordionButton={({ hasAccordionPanel, isExpanded }) => (
        <BorrowingBaseSummaryAccordionRow
          label={commonUtils.toTitleCase(loanProductSlug)}
          columnValues={totalEligibilityKickoutForLps}
          valueType="currency"
          hasAccordionPanel={hasAccordionPanel}
          isExpanded={isExpanded}
          showComparisonAndDifferenceColumns={
            showComparisonAndDifferenceColumns
          }
          showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
          fieldName="totalEligibilityKickout"
        />
      )}
      accordionPanel={
        <Box>
          {_.map(
            eligibilityCriteriaCalculationsForLoanProduct.minuendBorrowingBaseReport,
            (minuendCalc, index) => {
              const minuendEligibilityReduction =
                minuendCalc.eligibilityReduction;

              const subtrahendEligibilityReduction = _.find(
                eligibilityCriteriaCalculationsForLoanProduct.subtrahendBorrowingBaseReport,
                ({ eligibilityCriteriaKey }) =>
                  eligibilityCriteriaKey === minuendCalc.eligibilityCriteriaKey,
              )?.eligibilityReduction;

              const referenceEligibilityReduction = _.find(
                eligibilityCriteriaCalculationsForLoanProduct.referenceBorrowingBaseReport,
                ({ eligibilityCriteriaKey }) =>
                  eligibilityCriteriaKey === minuendCalc.eligibilityCriteriaKey,
              )?.eligibilityReduction;

              return (
                <BorrowingBaseSummaryAccordionSubRow
                  key={`sub-row-eligibility-${minuendCalc.eligibilityCriteriaKey}`}
                  label={getNumberedSubRowLabel(
                    index,
                    minuendCalc.eligibilityCriteriaKey,
                  )}
                  columnValues={{
                    minuendValue: minuendEligibilityReduction,
                    subtrahendValue: subtrahendEligibilityReduction,
                    referenceValue: referenceEligibilityReduction,
                  }}
                  valueType="currency"
                  showComparisonAndDifferenceColumns={
                    showComparisonAndDifferenceColumns
                  }
                  showValueAndReferenceColumnsOnly={
                    showValueAndReferenceColumnsOnly
                  }
                  fieldName="eligibilityReduction"
                />
              );
            },
          )}
        </Box>
      }
    />
  );
};
